import { Alert, Button, TextField } from '@mui/material';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { PiCheckCircleDuotone } from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import { useCreateContactMessage } from '~/api/contact_message';
import SpinnerTw from '~/components/SpinnerTw';
import { GrowInOut } from '~/shared/animations/GrowInOut';
import { useCommonAppContext } from '~/shared/contexts/CommonAppContext';
import Captcha from '~/shared/utils/Captcha';
import { RenderError } from '~/shared/utils/errorUtils';
import { getLcn } from '~/utils/labeled-classnames';
import contactFormTemplates from './contactFormTemplates';

export default function ContactForm() {
  const [captchaVerified, setCaptchaVerified] = useState(false)
  const { sessionUser, hasFetchedSessionUser } = useCommonAppContext();
  const { contact_template } = useParams<{ contact_template?: keyof typeof contactFormTemplates }>();
  const contactFormTemplateText = contact_template ? contactFormTemplates[contact_template] : '';

  const lcn = getLcn({
    contactForm: 'flex flex-col gap-12 transition-all',
  });

  const {
    data: contactMessage,
    mutate: createContactMessage,
    isPending,
    isError,
    error,
    isSuccess
  } = useCreateContactMessage()

  const delayedSetcaptchaVerified = (verified: boolean) => {
    setTimeout(() => {
      setCaptchaVerified(verified);
    }, 1000); // Delay to allow GrowInOut animation to complete

    setTimeout(() => {
      document.getElementById('contact-form')?.getElementsByTagName('input')[0].focus();
    }, 1300); // Delay to allow GrowInOut animation to complete
  }
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const name = (form.elements.namedItem('name') as HTMLInputElement)?.value;
    const subject = (form.elements.namedItem('subject') as HTMLInputElement)?.value;
    const content = (form.elements.namedItem('content') as HTMLInputElement)?.value;
    const email = (form.elements.namedItem('email') as HTMLInputElement)?.value;

    createContactMessage({
      user_id: sessionUser?.id,
      name,
      subject,
      content,
      email,
    });
  };

  const disabled = !captchaVerified || isPending;

  if (!hasFetchedSessionUser) return <SpinnerTw />;

  return (
    <>

      <div className={lcn.contactForm}>

        <GrowInOut show={!captchaVerified} >
          <div className='sm:w-[340px] sm:mx-auto'>
            <Captcha length={4} setCaptchaVerified={delayedSetcaptchaVerified} hideOnVerify />
          </div>
        </GrowInOut>

        {isError && <RenderError error={error as AxiosError} useBox />}

        {isSuccess ? (
          <Alert icon={<PiCheckCircleDuotone fontSize="inherit" />} severity="success">
            Contact message sent successfully! We will get back to you as soon as possible.
          </Alert>
        ) : (
          <form id="contact-form" onSubmit={handleSubmit} className='flex flex-col gap-4' >
            <div className='flex flex-col sm:flex-row gap-4'>
              <TextField id="name" label="Name" variant="filled" className='w-full' required disabled={disabled} defaultValue={sessionUser?.name} />
              <TextField id="email" label="Email" variant="filled" className='w-full' required disabled={disabled} type='email' defaultValue={sessionUser?.email} />
            </div>
            <TextField id="subject" label="Subject" variant="filled" className='w-full' disabled={disabled} required />
            <TextField
              id="content"
              label="Message"
              variant="filled"
              className='w-full'
              multiline
              minRows={3}
              disabled={disabled}
              defaultValue={contactFormTemplateText}
              required />
            <Button type='submit' variant="contained" color="primary" disabled={disabled}>Submit</Button>
          </form>
        )}
      </div >
    </>
  );
}; 